import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IconFileTypePdf } from '@tabler/icons-react';
import { useMeeting } from 'src/api/queries/meetings/useMeeting';
import { useMeetingPdf } from 'src/api/queries/meetings/useMeetingPdf';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { useJobs } from 'src/hooks/useJobs';
import { Header } from 'src/design/organisms/Header';
import { PdfViewer } from 'src/design/organisms/PdfViewer/PdfViewer';
import { usePdfFile } from 'src/design/molecules/usePdfFile';

export const ExportMeeting: React.FC = () => {
  const { selectedJob } = useJobs();
  const navigate = useNavigate();
  const params: any = useParams();
  const { data: meeting, isLoading: loading } = useMeeting(
    selectedJob?.id,
    params.meetingId,
  );
  const meetingPdf = useMeetingPdf(selectedJob?.id, params.meetingId);
  const pdf = usePdfFile(meetingPdf.data);
  const meetingTitle = meeting?.title ?? '';

  if (!selectedJob) return null;

  if (loading || !meeting) {
    return (
      <Group justify="center" mt="xl">
        <Loader />
      </Group>
    );
  }

  return (
    <>
      <Header
        title="Meeting Export"
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            {
              text: 'Meeting Minutes',
              href: `/jobs/${selectedJob.id}/meetings`,
            },
            {
              text: meetingTitle.length > 0 ? meetingTitle : 'Untitled Meeting',
              href: `/jobs/${selectedJob.id}/meetings/edit/${params.meetingId}`,
            },
            { text: 'Export' },
          ],
        }}
        helpText="View and export the meeting minutes as a PDF document"
        helpTitle="Meeting Export"
      />

      <Group justify="flex-start" mb="md">
        <SecondaryButton
          onClick={() =>
            navigate(
              `/jobs/${selectedJob.id}/meetings/edit/${params.meetingId}`,
            )
          }
        >
          Back to Meeting
        </SecondaryButton>
        <BasicButton
          onClick={pdf.download}
          disabled={!pdf.url || meetingPdf.isFetching}
          leftSection={<IconFileTypePdf size={18} />}
        >
          Download PDF
        </BasicButton>
      </Group>

      <PdfViewer
        src={pdf.url}
        title="Meeting Minutes PDF"
        loading={meetingPdf.isFetching}
        emptyText={
          meetingPdf.isError
            ? 'The meeting minutes PDF could not be generated.'
            : 'No PDF to display.'
        }
      />
    </>
  );
};
